'use client';

import {
  memo, forwardRef, ReactNode, useEffect,
} from 'react';
import { CardAnimations } from '@type/card';
import { cn } from '@utils/cn';
import {
  Anchor, Avatar, Button, Card, LinkStyled, Modal,
  Dropdown, DropdownButton, DropdownItems, DropdownItem,
} from '@components/common';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/20/solid';
import { AddressBookSolidIcon } from '@components/common/Icons';
import { useTranslation } from 'next-i18next';
import { useTalentCardActions } from '@components/templates/employer/cards-lists/hooks/useTalentCardActions';
import { SendMessageModalContent } from '@components/templates/cv/partials/modals/SendMessageModalContent';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

// Interface
// ************************************
interface AlumniCardProps {
  data: ApiSchema<'AlumniCardData'>,
  animation?: CardAnimations,
  trackingId?: string,
  className?: string,
  locale?: Locale
}

/**
 * @description Alumni card component. To be used in the Employer profile page.
 * Displays the talent profile picture, name, and experience and opens a modal with CV detail info.
 *
 * - data: *ApiSchema<'AlumniCardData'>* - The alumni data. Required.
 * - animation: *CardAnimations* - The animation for the card. Optional. Default is 'rise'.
 * - trackingId: *string* - Tracking request id. Optional.
 * - className: *string* - Additional classes for the card, for styling purposed. Optional.
 */

const RenderLinkableContent = ({ slug, href, children } : { slug?: string; href:string; children:ReactNode }) => {
  if (slug) {
    return (
      <LinkStyled className="z-1" styling="none" href={href} scroll={false}>
        {children}
      </LinkStyled>
    );
  }
  return <>{children}</>;
};

export const AlumniCard = forwardRef<HTMLDivElement, AlumniCardProps>((props, ref) => {
  // Destructure props
  const {
    data,
    animation = 'rise',
    trackingId,
    className,
    locale,
  } = props;

  // Variables
  // *****************************************
  const {
    name, slug, imgUrl, experience,
  } = data;
  const cardLinkUrlParams = trackingId ? `?rid=${trackingId}` : '';
  const cardLink = `/cv/${slug}${cardLinkUrlParams}`;

  const { router } = useExtendedRouter();
  const { t } = useTranslation('common');
  const { headerInfoData } = useHeaderInfoData(locale as string);
  const isCompany = headerInfoData?.role === 'COMPANY';
  const isUser = headerInfoData?.role === 'USER';
  const isAuthenticated = !!headerInfoData;

  // Common classes
  // *****************************************
  const cardClasses = cn('relative rounded-md bg-surface shadow-xs', className);


  const {
    unlockWithMessage,
    unlockByContactData,
    toggleSendMessageModal,
    displaySendMessageModal,
    displayOpenCvDetail,
  } = useTalentCardActions({
    userSlug: slug ?? '',
    locale: locale ?? 'ro',
    revalidatePage: (() => {}),
    hasInterviewFolder: false,
    interviewFolders: [],
  });

  useEffect(() => {
    if (displayOpenCvDetail) {
      void router.push(`/cv/${slug}`);
    }
  }, [displayOpenCvDetail, slug, router, locale]);


  const renderContactAction = () => {
    if (!isAuthenticated) {
      return (
        <LinkStyled
          href={`/login?targetPath=${encodeURIComponent(`/${locale}/cv/${slug}`)}`}
          size="sm"
          rounding="full"
          styling="none"
          className="mt-2 min-w-full rounded-full bg-secondary/10 px-4.5 py-2.5 text-center text-sm font-medium text-secondary hover:bg-secondary hover:text-surface"
        >
          {t('cv.detail.action.contact.label')}
        </LinkStyled>
      );
    }

    if (isUser) {
      return (
        <Anchor
          href="account_user_type"
          symfonyRoute
          symfonyParams={{ _target_path: `/${locale}/${slug}` }}
          size="sm"
          rounding="full"
          styling="none"
          className="mt-2 min-w-full rounded-full bg-secondary/10 px-4.5 py-2.5 text-center text-sm font-medium text-secondary hover:bg-secondary hover:text-surface"
        >
          {t('cv.detail.action.contact.label')}
        </Anchor>
      );
    }

    if (isCompany) {
      return (
        <Dropdown>
          <DropdownButton
            as={Button}
            size="sm"
            rounding="full"
            styling="none"
            className="mt-2 min-w-full rounded-full bg-secondary/10 px-4.5 py-2.5 text-sm font-medium text-secondary hover:bg-secondary hover:text-surface"
          >
            {t('cv.detail.action.contact.label')}
          </DropdownButton>
          <DropdownItems
            animation="slideUp"
            className="bottom-full z-[99] mb-2 flex min-w-full flex-col items-start gap-3 !rounded-md p-4"
          >
            <DropdownItem
              as={Button}
              styling="text"
              color="ink"
              onClick={unlockWithMessage}
            >
              <ChatBubbleLeftEllipsisIcon className="mr-2 size-4" />
              <span className="flex items-center text-sm">
                {t('cv.detail.action.message.label')}
              </span>
            </DropdownItem>
            <DropdownItem
              as={Button}
              styling="text"
              color="ink"
              onClick={unlockByContactData}
            >
              <AddressBookSolidIcon className="mr-2.5 h-4 w-3.5 fill-ink" />
              <span className="text-sm">
                {t('cv.detail.action.see_contact.label')}
              </span>
            </DropdownItem>
          </DropdownItems>
        </Dropdown>
      );
    }

    return null;
  };

  // Render component
  // *****************************************
  return (
    <Card ref={ref} animation={animation} className={cardClasses}>
      {/* Card Body: Alumni avatar */}
      <Card.Body className="!grow-0 !p-0">
        <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-t-md">
          <RenderLinkableContent slug={slug} href={cardLink}>
            <Avatar
              size="3xl"
              name={name}
              alt={name}
              imgPath={imgUrl}
              aspectRatio="square-fill"
              imgFolder="resume_image"
              id={(name || '').charCodeAt(0)}
              className="max-h-full max-w-full"
              rounding="none"
            />
          </RenderLinkableContent>

        </div>
      </Card.Body>

      {/* Card Footer: Alumni name + experience */}
      <Card.Footer className="flex h-full flex-col justify-between space-y-0.5 px-3 py-6">
        {/* Alumni name */}

        <div className="mb-4">
          <RenderLinkableContent slug={slug} href={cardLink}>
            <h3 className="font-bold">{name}</h3>
          </RenderLinkableContent>

          {/* Alumnus experience */}
          {experience && (
            <RenderLinkableContent slug={slug} href={cardLink}>
              <div className="text-sm text-ink-medium">
                {experience.title && (
                  <span className="block">
                    {experience.title}
                    {experience.location && ` (${experience.location})`}
                  </span>
                )}
              </div>
            </RenderLinkableContent>
          )}
        </div>


        {renderContactAction()}


        {/* Modal for message send */}
        <Modal
          isOpen={displaySendMessageModal}
          body={(
            <SendMessageModalContent
              locale={locale}
              candidateName={name || ''}
              onSendSuccess={() => toggleSendMessageModal()}
              onClose={() => { toggleSendMessageModal(); }}
              slug={slug || ''}
            />
          )}
          onClose={() => { toggleSendMessageModal(); }}
        />


      </Card.Footer>


    </Card>
  );
});

/**
 * @description Memoized AlumniCard component
 */
export const AlumniCardMemo = memo(AlumniCard);
